/**
 * R-72 — Report output distribution statistics (markdown + structured fields).
 */
import type { SiteRagReport } from '../../types/siteRag';
import { extractSections, renderReportMarkdownBody } from './reportTextExtract';

export interface DistributionStats {
  wordCount: number;
  charCount: number;
  sectionCount: number;
  h2Count: number;
  h3Count: number;
  totalCitations: number;
  avgCitationsPerSection: number;
  maxCitationsPerSection: number;
  tableCount: number;
  figureCount: number;
  caveatCount: number;
}

export const DISTRIBUTION_STAT_FIELDS: Array<keyof DistributionStats> = [
  'wordCount',
  'charCount',
  'sectionCount',
  'h2Count',
  'h3Count',
  'totalCitations',
  'avgCitationsPerSection',
  'maxCitationsPerSection',
  'tableCount',
  'figureCount',
  'caveatCount',
];

export const DISTRIBUTION_STAT_LABELS: Record<keyof DistributionStats, string> = {
  wordCount: 'Word count',
  charCount: 'Character count',
  sectionCount: 'Section count',
  h2Count: 'H2 headings',
  h3Count: 'H3 headings',
  totalCitations: 'Total [Ref N] citations',
  avgCitationsPerSection: 'Avg citations per section',
  maxCitationsPerSection: 'Max citations in one section',
  tableCount: 'Markdown tables',
  figureCount: 'Figures',
  caveatCount: 'Caveats / limitations',
};

const REF_TOKEN_RE = /\[Ref\s+\d+\]/gi;
const H2_RE = /^##\s+/gm;
const H3_RE = /^###\s+/gm;
const FIGURE_RE = /!\[[^\]]*\]\([^)]+\)/g;
const CAVEAT_H3_RE = /^###\s+.*\b(caveat|limitation|assumption)s?\b/im;
const CALLOUT_RE = /^>\s*(?:\*\*)?(?:Caveat|Limitation|Assumption)/im;

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function countTableBlocks(text: string): number {
  const lines = text.split('\n');
  let count = 0;
  let inTable = false;
  let tableRows = 0;
  for (const line of lines) {
    if (/^\s*\|.+\|.+\|\s*$/.test(line)) {
      if (!inTable) {
        inTable = true;
        tableRows = 0;
      }
      tableRows += 1;
    } else if (inTable) {
      if (tableRows >= 2) count += 1;
      inTable = false;
      tableRows = 0;
    }
  }
  if (inTable && tableRows >= 2) count += 1;
  return count;
}

function countCaveats(text: string): number {
  let count = 0;
  for (const line of text.split('\n')) {
    if (CAVEAT_H3_RE.test(line) || CALLOUT_RE.test(line)) count += 1;
    if (/\b(caveat|limitation|assumption)s?\b/i.test(line) && /^#{1,3}\s/.test(line)) {
      count += 1;
    }
  }
  return count;
}

function countCitations(text: string): number {
  return (text.match(REF_TOKEN_RE) ?? []).length;
}

export function computeStats(report: SiteRagReport): DistributionStats {
  // R-72 fix — measure the RENDERED MARKDOWN BODY (the same prose a reader
  // sees: `## {label}` per section + section narrative), NOT the `# <id>\n<dump>`
  // key:value serialization. The old `reportFullText` dump emitted section
  // headings as H1 (`#`), so structurally-complete typed reports counted H2=0 /
  // H3=0 and tripped the "likely incomplete generation" hard block as a FALSE
  // POSITIVE — which wrongly set qualityHold and barred export.
  const fullText = renderReportMarkdownBody(report);
  const sections = extractSections(report);
  const perSectionCitations = sections.map((s) => countCitations(s.content));
  const totalCitations = perSectionCitations.reduce((a, b) => a + b, 0);
  const sectionCount = Math.max(sections.length, 1);
  const avgCitationsPerSection = totalCitations / sectionCount;
  const maxCitationsPerSection = perSectionCitations.length > 0
    ? Math.max(...perSectionCitations)
    : 0;

  return {
    wordCount: countWords(fullText),
    charCount: fullText.length,
    sectionCount: sections.length,
    h2Count: (fullText.match(H2_RE) ?? []).length,
    h3Count: (fullText.match(H3_RE) ?? []).length,
    totalCitations,
    avgCitationsPerSection: Math.round(avgCitationsPerSection * 100) / 100,
    maxCitationsPerSection,
    tableCount: countTableBlocks(fullText),
    figureCount: (fullText.match(FIGURE_RE) ?? []).length,
    caveatCount: countCaveats(fullText),
  };
}
